import React, { useState, useRef, useEffect } from "react";
import { api, sendMessage, compressImage, uploadMedia } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Camera, CheckCircle2, X, Send, Sparkles } from "lucide-react";
import { VoiceDraftButton } from "./VoiceDraftButton";

/**
 * Field resolve flow: the installer describes the fix (text + optional photo),
 * posts it into the conversation, then confirms whether the issue is resolved.
 */
export function ResolveIssueDialog({ open, onClose, message, conversation, onResolved }) {
  const { user, t } = useApp();
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null); // {base64, dataUrl}
  const [step, setStep] = useState("form"); // form | confirm
  const [busy, setBusy] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false); // AI draft filled from speech
  const fileRef = useRef(null);

  const issueId = message?.meta?.issue_id;
  const issueTitle = message?.meta?.title || "";
  const jobId = message?.meta?.linked_job_id || conversation?.job_id || null;

  useEffect(() => {
    if (open) {
      setText("");
      setPhoto(null);
      setStep("form");
      setVoiceReady(false);
    }
  }, [open]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { base64, dataUrl } = await compressImage(file);
      setPhoto({ base64, dataUrl });
    } catch (err) {
      toast.error("Photo failed");
    }
    e.target.value = "";
  };

  const post = async () => {
    if (!text.trim() && !photo) return;
    setBusy(true);
    try {
      let mediaIds = [];
      if (photo) {
        const m = await uploadMedia({
          base64: photo.base64,
          jobId,
          category: "correction",
          uploadedBy: user.id,
        });
        mediaIds = [m.id];
      }
      const meta = { resolution_issue_id: issueId, issue_title: issueTitle, ...(photo ? { category: "correction" } : {}) };
      // Keep the job badge on the resolution message (shared with the job)
      if (!conversation?.job_id && jobId) {
        meta.linked_job_id = jobId;
        meta.link_visibility = "shared";
      }
      // The resolution is a reply to the issue message: quoted + one-tap jump back to it
      const senderName = (conversation?.participants || []).find((p) => p.id === message?.sender_id)?.name || "";
      const reply = message ? {
        message_id: message.id,
        sender_id: message.sender_id,
        sender_name: senderName,
        content: issueTitle || (message.content || "").slice(0, 120),
        type: "issue",
      } : null;
      await sendMessage({
        conversation_id: conversation.id,
        sender_id: user.id,
        type: photo ? "photo" : "text",
        content: text.trim(),
        media_ids: mediaIds,
        meta,
        reply_to: reply,
      });
      setStep("confirm");
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const finish = async (resolved) => {
    if (resolved && issueId) {
      setBusy(true);
      try {
        await api.patch(`/issues/${issueId}`, { status: "resolved", user_id: user.id });
        toast.success(`${t("issue")}: ${t("status_resolved")}`);
        onResolved && onResolved(issueId);
      } catch (e) {
        toast.error("Failed");
      } finally {
        setBusy(false);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="resolve-issue-dialog">
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" /> {t("resolve_issue_title")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-2 text-sm bg-muted/60 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--issue-open))] shrink-0" />
              <span className="font-medium truncate">{issueTitle}</span>
            </div>
            {/* Voice-first: speak the fix -> AI cleans it into the resolution note */}
            <VoiceDraftButton
              kind="resolution"
              label={voiceReady ? t("speak_again") : t("speak_resolution")}
              onDraft={(d) => { setText(d.note || ""); setVoiceReady(true); }}
            />
            {voiceReady && (
              <div data-testid="resolve-voice-ready-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <Sparkles className="h-4 w-4" /> {t("draft_ready")}
                </div>
                <div className="text-xs text-muted-foreground">{t("add_photo_question")}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="resolve-voice-add-photo"
                    onClick={() => fileRef.current?.click()}
                  >
                    <Camera className="h-4 w-4 mr-1.5 text-primary" /> {t("add_photo")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    data-testid="resolve-voice-send-now"
                    onClick={post}
                    disabled={busy || !text.trim()}
                    className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white"
                  >
                    <Send className="h-4 w-4 mr-1.5" /> {busy ? t("uploading") + "..." : t("send_now")}
                  </Button>
                </div>
              </div>
            )}
            <Textarea
              data-testid="resolve-note-input"
              placeholder={t("resolution_note_ph")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              autoFocus
            />
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
            {photo ? (
              <div className="relative inline-block">
                <img src={photo.dataUrl} alt="" className="h-28 rounded-lg border object-cover" />
                <button
                  data-testid="resolve-photo-remove"
                  onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-white flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Button variant="outline" data-testid="resolve-add-photo" onClick={() => fileRef.current?.click()} className="justify-start">
                <Camera className="h-4 w-4 mr-2 text-primary" /> {t("add_photo")}
              </Button>
            )}
            <DialogFooter>
              <Button
                data-testid="resolve-post-button"
                onClick={post}
                disabled={busy || (!text.trim() && !photo)}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" /> {busy ? t("uploading") + "..." : t("send")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">{t("issue_resolved_q")}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">{issueTitle}</p>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button
                data-testid="resolve-confirm-yes"
                onClick={() => finish(true)}
                disabled={busy}
                className="w-full bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" /> {t("yes_mark_resolved")}
              </Button>
              <Button
                data-testid="resolve-confirm-no"
                variant="outline"
                onClick={() => finish(false)}
                disabled={busy}
                className="w-full"
              >
                {t("no_keep_open")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
